import { WEBMAP_BASELAYER_ID_PREFIX } from '@nextgis/ngw-kit';
import UrlRuntimeParams from '@nextgis/url-runtime-params';

import { defined } from '../../@nextgis/packages/utils/src';
import { WEBMAP_TERRAIN_ID_PREFIX } from '../constants';
import { Scene3DAdapter } from '../plugins/threedim/Scene3DAdapter';

import { setCameraPosition, shareKeys } from './cameraPosition';

import type { Scene3DTreeItemMain } from '../plugins/threedim/interfaces';
import type { ShareKey } from '../types/ShareKey';
import type { TerrainLayerAdapterOptions } from '../types/TerrainLayerAdapterOptions';
import type { NgwMap } from '@nextgis/ngw-map';

const urlParams = new UrlRuntimeParams();

export function applyUrlParams(ngwMap: NgwMap): void {
  const opt = getShareUrlParams();
  const { position, up, direction, base, resources, terrain } = opt;
  if (ngwMap.mapAdapter.map && (position || up || direction)) {
    setCameraPosition(ngwMap.mapAdapter.map, { position, up, direction });
  }
  if (base) {
    setBaselayer(ngwMap, base);
  }
  if (terrain) {
    setTerrain(ngwMap, terrain);
  }
  if (resources) {
    setResources(ngwMap, resources);
  }
}

function setResources(ngwMap: NgwMap, resources_: string) {
  const resources = resources_.split(',').map(Number);

  ngwMap.getNgwLayers().then((ngwLayers) => {
    for (const l in ngwLayers) {
      const layerDef = ngwLayers[l];

      if (layerDef) {
        if (layerDef.layer instanceof Scene3DAdapter) {
          const dep = layerDef.layer.getDependLayers();
          for (const d of dep) {
            if (d.layer) {
              const id = (d.item as any as Scene3DTreeItemMain).resource_id;
              d.properties.set('visibility', resources.indexOf(id) !== -1);
            }
          }
        } else {
          ngwMap.toggleLayer(
            layerDef.layer,
            resources.indexOf(layerDef.resourceId) !== -1,
          );
        }
      }
    }
  });
}

function setBaselayer(ngwMap: NgwMap, base: string) {
  if (base === 'blank') {
    const activeBaselayer = ngwMap.getActiveBaseLayer();
    activeBaselayer && ngwMap.removeLayer(activeBaselayer);
  } else {
    const baselayers = ngwMap.getBaseLayers();
    const baseLayerId = WEBMAP_BASELAYER_ID_PREFIX + base;
    const baselayer = baselayers.find((x) => {
      const layerId =
        x.id && x.id.startsWith(WEBMAP_BASELAYER_ID_PREFIX) ? x.id : false;
      if (layerId) {
        return layerId === baseLayerId;
      }
    });
    if (baselayer) {
      ngwMap.showLayer(baselayer);
    }
  }
}

async function setTerrain(ngwMap: NgwMap, terrain: string) {
  const terrainLayers = Object.values(ngwMap.allLayers()).filter(
    (x) => (x.options as TerrainLayerAdapterOptions).terrain,
  );
  for (const t of terrainLayers) {
    await ngwMap.hideLayer(t);
  }
  if (terrain !== 'blank') {
    const terrainId = WEBMAP_TERRAIN_ID_PREFIX + terrain;
    const terrainLayer = terrainLayers.find((x) => {
      const layerId =
        x.id && x.id.startsWith(WEBMAP_TERRAIN_ID_PREFIX) ? x.id : false;
      if (layerId) {
        return layerId === terrainId;
      }
    });
    if (terrainLayer) {
      await ngwMap.showLayer(terrainLayer);
    }
  }
}

function getShareUrlParams(): Partial<Record<ShareKey, string>> {
  const params = urlParams.params();
  const shareParams: Partial<Record<ShareKey, string>> = {};
  for (const key_ in params) {
    const key = key_ as ShareKey;
    if (shareKeys.indexOf(key) !== -1) {
      const param = params[key];
      if (defined(param)) {
        shareParams[key] = param;
      }
    }
  }
  return shareParams;
}
